import React, {
  forwardRef,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { FieldProps } from "../FieldBase";
import { FormControl } from "../FormControl";
import { FormHelperText } from "../FieldBase/FormHelperText";
import { InputBase, InputBaseProps } from "../InputBase";
import { InputLabel } from "../InputLabel";
import { LocaleContext } from "../LocaleProvider";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useFieldGroup } from "../FieldGroup";
import { useInputValue } from "../hooks/useInputValue";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useValidation } from "../FieldBase/useValidation";
import { NumberFormatOptions } from "./NumberFormatOptions";
import {
  NumberFieldInputWrapper,
  NumberFieldInputWrapperContext,
} from "./NumberFieldInputWrapper";

interface CommonNumberFieldProps extends Omit<
  InputBaseProps,
  "defaultValue" | "value" | "onChange"
> {
  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
   */
  options?: NumberFormatOptions;
  type?: never;
  children?: never;
  /**
   * 入力が受け付ける最大値を指定します。
   *
   * @default Number.MAX_SAFE_INTEGER
   */
  max?: number;
  /**
   * 入力が受け付ける最小値を指定します。
   *
   * @default Number.MIN_SAFE_INTEGER
   */
  min?: number;
}

export interface NumberFieldProps
  extends FieldProps<number | undefined>, CommonNumberFieldProps {}

const toLocaleValue = (
  value?: number,
  locale?: string,
  options?: NumberFormatOptions,
): string | undefined => {
  return value === undefined ? "" : value.toLocaleString(locale, options);
};

const equalNumber = (a: number | undefined, b: number | undefined): boolean => {
  if (a === b) {
    return true;
  }
  // NaNとNaNはtrueにならないが等しいとみなす
  if (Number.isNaN(a) && Number.isNaN(b)) {
    return true;
  }
  return false;
};

const additionalValidations = [
  (value?: number) => {
    if (Number.isNaN(value)) {
      if (navigator && /^ja\b/.test(navigator.language)) {
        return "不正でない数値を入力してください";
      }
      return "Enter a valid number";
    }
    return;
  },
] satisfies NumberFieldProps["customValidations"];

export const NumberField = forwardRef<
  HTMLDivElement,
  Readonly<NumberFieldProps>
>((props, rootRef) => {
  const {
    validationMessages,
    customValidations,
    style,
    className,
    label,
    options,
    onChange,
    onFocus,
    defaultValue,
    disabled,

    value: _propsValue,
    readOnly: propsReadOnly,
    loading: propsLoading,
    helperText,
    invalid,
    max = Number.MAX_SAFE_INTEGER,
    min = Number.MIN_SAFE_INTEGER,
    step = 1,
    id,
    inputRef,
    ...others
  } = props;
  const locale = useContext(LocaleContext);
  const ref = useRef<HTMLInputElement>(null);
  const refs = useMergeRefs(inputRef, ref);

  const [value, setValue, isControlled] = useInputValue(props);
  const { loading, readOnly } = useFieldGroup({
    loading: propsLoading,
    readOnly: propsReadOnly,
    onLoaded: () => {
      if (isControlled === false) {
        setValue(defaultValue);
      }
    },
  });

  const mergedCustomValidations = useMemo(() => {
    return customValidations
      ? [...customValidations, ...additionalValidations]
      : additionalValidations;
  }, [customValidations]);

  const validation = useValidation({
    ref,
    validationMessages,
    customValidations: mergedCustomValidations,
    value,
  });

  // input要素の実際の値を管理している。これにより、`.0`や`.20`など入力途中の表示を維持できる。
  const [inputValue, setInputValue] = useState(
    value === undefined ? "" : `${value}`,
  );

  // 制御コンポーネントとして扱われる場合、外部からの値の変更を検知してinput要素の値を更新する。
  const prevPropsValue = useRef(props.value);

  // eslint-disable-next-line react-hooks/refs
  if (isControlled && !equalNumber(value, prevPropsValue.current)) {
    // eslint-disable-next-line react-hooks/refs
    prevPropsValue.current = props.value;
    setInputValue(value === undefined ? "" : `${value}`);
  }

  // フォーカスが外れた場合、入力途中の値を破棄するためにinputValueを更新する
  const handleBlur = useCallback(() => {
    setInputValue(value === undefined ? "" : `${value}`);
  }, [value]);

  const [touched, touchedProps] = useDitectInputTouched(onFocus);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;

      const next = value === "" ? undefined : Number(value);
      setInputValue(value);
      setValue(next);
      if (onChange) {
        onChange(next, event);
      }
    },
    [onChange, setValue],
  );

  const handleWheel: React.WheelEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      event.currentTarget.blur();
    },
    [],
  );

  const internalInputId = useId();
  const inputId = id ?? internalInputId;

  return (
    <FormControl style={style} className={className} ref={rootRef}>
      <InputLabel
        aria-disabled={disabled}
        required={props.required}
        loading={loading}
        htmlFor={inputId}
      >
        {label}
      </InputLabel>
      <NumberFieldInputWrapperContext.Provider
        value={
          validation.type === "badInput"
            ? "Invalid number"
            : toLocaleValue(value, locale, options)
        }
      >
        <InputBase
          loading={loading}
          disabled={disabled}
          id={inputId}
          ref={refs}
          type="number"
          renderInputWrapper={NumberFieldInputWrapper}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onWheel={handleWheel}
          readOnly={readOnly}
          data-invalid={!validation.valid}
          max={max}
          min={min}
          step={step}
          {...others}
          {...touchedProps}
        />
      </NumberFieldInputWrapperContext.Provider>

      <FormHelperText
        invalid={!validation.valid || invalid}
        loading={loading}
        touched={touched}
        value={value}
      >
        {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          validation.message || helperText
        }
      </FormHelperText>
    </FormControl>
  );
});
